import React, { PureComponent } from 'react'
import { Trans, withI18n, withI18nProps } from '@lingui/react'
import classNames from 'classnames'
import get from 'lodash/get'
import compose from 'lodash/fp/compose'

import urlFor, { QueryParams } from 'shared/tools/url-helper'
import subscriptionHelper from 'client/shared/helpers/subscription-helper'
import uiBox, { DecoratorProps } from 'client/shared/decorators/ui-box'

import { ReadablePrice } from 'client/shared/blocks/readable-price'
import ReadableDate from 'client/shared/blocks/readable-date'
import Button from 'client/shared/blocks/button'
import Spacer from 'client/shared/blocks/spacer'
import { StyledBadge } from 'client/shared/blocks/badge'
import CreditCardEditField from 'client/bookmate/blocks/credit-card-edit-field'
import SubscriptionLogo from 'client/bookmate/blocks/subscription-logo'

import './recurrent-subscription-card.styl'

import { RecurrentSubscription } from 'client/shared/types/subscription'
import { CancellationNotice } from './cancellation-notice'
import { CancellationReason } from 'client/bookmate/reducers/unsubscribe-reducer'
import {
  cancelYettelHungary,
  loadProducts,
} from 'client/bookmate/reducers/subscription-reducer'
import { RetentionOffer } from '../manage-subscriptions/retention-offer'
import { Locale } from 'client/shared/types/current-user'
import { DiscountBadge } from '../product-card/product-card'

type Props = DecoratorProps & {
  subscription: RecurrentSubscription
  cancelRecurrent: () => Promise<void>
  chargeForRecurrent: () => void
  showRetentionOffer: () => void
  toggleCancellationOffer: () => void
  login: string
  libraryParams?: { content_type: string; slug: string }
  cancellationReasons?: {
    variants: CancellationReason[]
    loading: boolean
  }
  query?: QueryParams
  userId?: number
  Inner?: () => JSX.Element
  ActionButtons?: () => JSX.Element
  offerDiscount?: boolean
} & withI18nProps

type State = {
  expanded: boolean
  isExpandable: boolean
  isRetentionOfferShown: boolean
  isCancellationNoticeOpen: boolean
  offerOption?: string
}

type CardState = {
  isCancellationNoticeOpen: boolean
}

class RecurrentSubscriptionCard extends PureComponent<Props, State> {
  state = {
    expanded: false,
    isExpandable: false,
    isRetentionOfferShown: false,
    isCancellationNoticeOpen: false,
    offerOption: '',
  }

  managementElement: RecurrentSubscriptionCardManage | null | undefined

  componentDidMount(): void {
    if (this.managementElement && this.managementElement.hasContent()) {
      this.setState({ isExpandable: true })
    }
  }

  showRetentionOffer = (arg?: string): void => {
    const { showRetentionOffer } = this.props
    if (showRetentionOffer) showRetentionOffer()
    this.setState({ isRetentionOfferShown: true, offerOption: arg })
  }

  onManageButtonClick = (): void => {
    const {
      subscription: { system },
      dispatch,
      userId,
      i18n,
    } = this.props

    if (system === 'telenor_bulgaria') {
      window.location.href = 'https://tlr.bg/MyT'
      return
    } else if (system === 'yettel_hungary') {
      const cancellationWarning = i18n.t`plan.cancel_recurrent_confirm`

      if (confirm(cancellationWarning)) {
        dispatch(cancelYettelHungary(userId as number))
        return dispatch(loadProducts())
      }
    } else if (system === 'mondia_pay') {
      window.location.href = '/p/api/v5/profile/mondia_pay_redirections'
      return
    }

    this.setState({ expanded: true })
  }

  showNoticeOrCancel() {
    this.setState({ isCancellationNoticeOpen: true })
  }
  keepSubscription() {
    this.setState({ isCancellationNoticeOpen: false, expanded: false })
  }

  closeOffer(): void {
    const { showRetentionOffer } = this.props
    if (showRetentionOffer) showRetentionOffer()
    this.setState({
      isRetentionOfferShown: false,
      expanded: false,
      isCancellationNoticeOpen: false,
    })
  }

  render(): JSX.Element {
    if (this.state.isRetentionOfferShown) {
      return (
        <RetentionOffer
          libraryParams={this.props.libraryParams}
          dispatch={this.props.dispatch}
          closeOffer={this.closeOffer.bind(this)}
          locale={this.props.library_lang as Locale}
          cancelRecurrent={this.props.cancelRecurrent}
          noInterest={Boolean(this.state.offerOption)}
        />
      )
    }
    const {
      subscription,
      i18n,
      ActionButtons,
      dispatch,
      cancellationReasons,
      toggleCancellationOffer,
      offerDiscount,
      Inner,
    } = this.props

    const shouldRenderManageButton =
      subscriptionHelper.isAutorenewableRecurrent(subscription) &&
      this.state.isExpandable &&
      !this.state.expanded &&
      !ActionButtons

    const expiryDate = new Date(
      (subscription?.subscribed_till as number) * 1000,
    )

    const isCouponActive =
      subscription?.stripe_extended_external_details?.discount

    return this.state.isCancellationNoticeOpen ? (
      <CancellationNotice
        dispatch={dispatch}
        cancellationReasonVariants={cancellationReasons?.variants}
        isLoading={Boolean(cancellationReasons?.loading)}
        i18n={i18n}
        expiryDate={subscription.terminate_on_cancel ? null : expiryDate}
        cancelRecurrent={this.props.cancelRecurrent}
        subscription={subscription}
        showRetentionOffer={this.showRetentionOffer.bind(this)}
        keepSubscription={this.keepSubscription.bind(this)}
      />
    ) : (
      <div
        className={`recurrent-subscription-card${
          offerDiscount || isCouponActive ? ' discounted' : ''
        }`}
      >
        {offerDiscount || isCouponActive ? (
          <DiscountBadge discountPercent={DISCOUNT_PERCENT} />
        ) : null}
        {this.renderCardTop()}
        {Inner ? <Inner /> : null}
        {subscription.retention_flow_special_offer_used && (
          <>
            <Spacer /> {this.getInnerCard()}
          </>
        )}
        <Spacer size={24} />
        {ActionButtons && <ActionButtons />}
        {shouldRenderManageButton && (
          <div className="recurrent-subscription-card__manage-button">
            <Button
              kind="transparent"
              onClick={this.onManageButtonClick}
              aria-label={i18n.t`buttons.manage`}
            >
              <Trans
                id={
                  subscription.system === 'yettel_hungary'
                    ? 'plan.cancel_recurrent'
                    : 'buttons.manage'
                }
              />
            </Button>
          </div>
        )}
        <div style={{ display: this.state.expanded ? 'block' : 'none' }}>
          <RecurrentSubscriptionCardManage
            {...this.props}
            toggleCancellationOffer={toggleCancellationOffer}
            showNoticeOrCancel={this.showNoticeOrCancel.bind(this)}
            showRetentionOffer={this.showRetentionOffer}
            ref={el => (this.managementElement = el)}
          />
        </div>

        {subscriptionHelper.hasRecurrentRenewalFailed(subscription) &&
          this.renderRenewalFailedNotification()}
      </div>
    )
  }

  renderCardTop(): JSX.Element {
    const { isMobileSize } = this.props

    if (isMobileSize()) {
      return this.renderMobileCardTop()
    } else {
      return this.renderDesktopCardTop()
    }
  }

  renderDesktopCardTop(): JSX.Element {
    const { Inner, subscription } = this.props
    return (
      <div className="recurrent-subscription-card__top">
        <div className="recurrent-subscription-card__top-left">
          <div className="recurrent-subscription-card__identity">
            {this.renderLabels()}
            {this.renderSubscriptionLogo()}
          </div>
          <div className="recurrent-subscription-card__description">
            {Inner || subscription.retention_flow_special_offer_used
              ? null
              : this.getInnerCard()}
          </div>
        </div>
        <div className="recurrent-subscription-card__top-right">
          {this.renderPrice()}
        </div>
      </div>
    )
  }

  getInnerCard(): JSX.Element {
    const { subscription } = this.props
    const expiryDate = new Date(
      (subscription?.subscribed_till as number) * 1000,
    )
    return (
      <>
        {subscription.description}
        <Spacer />
        {!subscription.autorenew_enabled && !subscription.terminate_on_cancel && (
          <>
            <Spacer />
            <Trans
              id="subscription.cancelled_notice"
              components={[
                <b>
                  <ReadableDate date={expiryDate} />
                </b>,
              ]}
            />
          </>
        )}
      </>
    )
  }

  renderMobileCardTop() {
    const { subscription } = this.props
    const expiryDate = new Date(
      (subscription?.subscribed_till as number) * 1000,
    )

    return (
      <div className="recurrent-subscription-card__top">
        <div className="recurrent-subscription-card__identity">
          {this.renderLabels()}
          {this.renderSubscriptionLogo()}
        </div>
        <Spacer />
        {this.renderPrice()}
        <Spacer />
        <div className="recurrent-subscription-card__description">
          {subscription.description}
          {!subscription.autorenew_enabled &&
            !subscription.terminate_on_cancel && (
              <>
                <Spacer />
                <Trans
                  id="subscription.cancelled_notice"
                  components={[
                    <b>
                      <ReadableDate date={expiryDate} />
                    </b>,
                  ]}
                />
              </>
            )}
        </div>
      </div>
    )
  }

  renderLabels(): JSX.Element {
    const labels = this.props.subscription.labels.map((label, index) => (
      <StyledBadge key={index} color={label.color}>
        {label.name}
      </StyledBadge>
    ))

    return <div className="recurrent-subscription-card__labels">{labels}</div>
  }

  renderSubscriptionLogo(): JSX.Element {
    return <SubscriptionLogo {...this.props.subscription} />
  }

  renderPrice(): JSX.Element | null {
    const {
      subscription: {
        price,
        currency,
        duration,
        stripe_extended_external_details,
      },
      offerDiscount,
    } = this.props

    const isCouponActive = stripe_extended_external_details?.discount

    if (
      typeof price === 'number' &&
      typeof currency === 'string' &&
      typeof duration === 'string'
    ) {
      return (
        <div className="recurrent-subscription-card__price">
          {offerDiscount || isCouponActive ? (
            <div className="recurrent-subscription-card__amount">
              <ReadablePrice
                price={price}
                currency={currency}
                kind="short"
                className="greyed-out"
              />
              <ReadablePrice
                price={price * DISCOUNT}
                currency={currency}
                kind="short"
              />
            </div>
          ) : (
            <div className="recurrent-subscription-card__amount">
              <ReadablePrice price={price} currency={currency} kind="short" />
            </div>
          )}
          <div className="recurrent-subscription-card__period">
            <Trans id={`plan.per_${duration}`} />
          </div>
        </div>
      )
    }
    return null
  }

  renderRenewalFailedNotification(): JSX.Element {
    return (
      <div
        className="recurrent-subscription-card__failed-renewal-notification"
        onClick={this.props.chargeForRecurrent}
      >
        <Trans id="subscription.renewal_failed" />
        {this.renderChargeButton()}
      </div>
    )
  }

  renderChargeButton(): JSX.Element | null {
    const { subscription } = this.props

    if (
      subscription.autorenew_enabled &&
      subscriptionHelper.isStripeSubscription(subscription)
    ) {
      return (
        <span className="recurrent-subscription-card__charge-button">
          <Trans id="plan.try_again" />
        </span>
      )
    }
    return null
  }
}

class RecurrentSubscriptionCardManage extends PureComponent<
  Props & {
    showRetentionOffer: () => void
    showNoticeOrCancel: () => void
    toggleCancellationOffer: () => void
  },
  CardState
> {
  container: HTMLElement | null | undefined

  getLabelColumnClasses() {
    return classNames([
      'recurrent-subscription-card__column-left',
      'recurrent-subscription-card__column-label',
    ])
  }

  hasContent() {
    return Boolean(this.container && this.container.innerHTML)
  }

  render(): JSX.Element {
    return (
      <div
        ref={el => (this.container = el)}
        className="recurrent-subscription-card__manage"
      >
        {this.renderPaymentInfoRow()}
        {this.renderRenewalRow()}
        {this.renderCancellationRow()}
      </div>
    )
  }

  renderPaymentInfoRow(): JSX.Element | null {
    const {
      login,
      query,
      subscription: { uuid: subscriptionId, autorenew_enabled },
    } = this.props
    const card = get(this.props, 'subscription.meta.card')

    if (!(subscriptionId && card && autorenew_enabled)) return null

    return (
      <div className="recurrent-subscription-card__row">
        <div className={this.getLabelColumnClasses()}>
          <Trans id="subscription.payment_system_info" />
        </div>
        <div className="recurrent-subscription-card__column-right">
          <CreditCardEditField
            query={query}
            login={login}
            subscriptionId={subscriptionId}
            card={card}
          />
        </div>
      </div>
    )
  }

  renderRenewalRow(): JSX.Element | null {
    const {
      subscription: { next_billing_at },
    } = this.props
    if (!next_billing_at) return null

    return (
      <div className="recurrent-subscription-card__row">
        <div className={this.getLabelColumnClasses()}>
          <Trans id="subscription.renewal_date" />
        </div>
        <div className="recurrent-subscription-card__column-right">
          <ReadableDate
            date={new Date(next_billing_at * 1000)}
            kind="date-month-long"
          />
        </div>
      </div>
    )
  }

  renderCancellationRow(): JSX.Element | null {
    const { subscription } = this.props
    if (subscriptionHelper.isAppleRecurrentSubscription(subscription)) {
      return this.renderCancelRecurrentForIOS()
    } else if (
      subscriptionHelper.isAndroidRecurrentSubscription(subscription)
    ) {
      return this.renderCancelRecurrent(true)
    } else if (subscriptionHelper.isUncancellableBundle(subscription)) {
      return this.renderBundleLink()
    } else if (subscription.actions.includes('cancel')) {
      return this.renderCancelRecurrent()
    }
    return null
  }

  renderCancelRecurrent(withoutFlow?: boolean): JSX.Element | null {
    const {
      subscription: { subscribed_till, autorenew_enabled },
      i18n,
      subscription,
      toggleCancellationOffer,
      showNoticeOrCancel,
      cancelRecurrent,
    } = this.props

    if (!autorenew_enabled) return null
    const expiryDate = subscribed_till
      ? new Date((subscribed_till as number) * 1000)
      : null

    const cancelActions = () => {
      toggleCancellationOffer()
      showNoticeOrCancel()
    }

    const cancelRecurrentActions = () => {
      cancelRecurrent().then()
    }

    return (
      <div className="recurrent-subscription-card__row">
        <div className="recurrent-subscription-card__column-left">
          <Button
            aria-label={i18n.t`plan.cancel_recurrent`}
            kind="transparent"
            type="inline"
            onClick={withoutFlow ? cancelRecurrentActions : cancelActions}
          >
            <Trans id="plan.cancel_recurrent" />
          </Button>
        </div>
        {expiryDate && !subscription.terminate_on_cancel && (
          <div className="recurrent-subscription-card__column-right">
            <span className="recurrent-subscription-card__cancellation-notice">
              <Trans
                id="subscription.cancellation_notice"
                components={[<ReadableDate date={expiryDate} />]}
              />
            </span>
          </div>
        )}
      </div>
    )
  }

  renderCancelRecurrentForIOS(): JSX.Element {
    const appstoreLocales = {
      default: 'en-us',
      ru: 'ru-ru',
      da: 'da-dk',
    }
    const { locale, isIOS, i18n } = this.props
    let link
    let messageId

    if (isIOS()) {
      link = urlFor.manageSubscriptionsInItunes()
      messageId = 'plan.cancel_recurrent'
    } else {
      link = `https://support.apple.com/${
        appstoreLocales[locale] || appstoreLocales.default
      }/HT202039`
      messageId = 'plan.how_to_cancel'
    }

    return (
      <div className="recurrent-subscription-card__row">
        <div className="recurrent-subscription-card__column-left">
          <Button
            kind="transparent"
            type="inline"
            path={link}
            aria-label={i18n.t`${messageId}`}
          >
            <Trans id={messageId} />
          </Button>
        </div>
      </div>
    )
  }

  renderBundleLink(): JSX.Element | null {
    const {
      subscription: { system },
      i18n,
    } = this.props
    let link

    if (system === 'telmorebundlerecurrent') {
      link = 'https://www.telmore.dk/website/mit-telmore/'
    } else if (system === 'youseebundlerecurrent') {
      link = 'https://selvbetjening.yousee.dk/more'
    } else if (system === 'plusbogbundlerecurrent') {
      link = 'https://www.plusbog.dk/profil/mit-medlemskab/'
    } else if (system === 'mondia_pay') {
      link = '/p/api/v5/profile/mondia_pay_redirections'
    } else if (system === 'personal_argentina') {
      return null
    }

    return (
      <Button
        kind="transparent"
        type="inline"
        path={link}
        aria-label={i18n.t`plan.settings`}
      >
        <Trans id="plan.settings" />
      </Button>
    )
  }
}

const wrappers = compose(uiBox, withI18n({ update: true }))

export default wrappers(RecurrentSubscriptionCard)

const DISCOUNT = 0.8
const DISCOUNT_PERCENT = 20
